import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ErrorBoundary } from "./lib/ErrorBoundary.js";
import { NotificationNavigationBridge } from "./components/NotificationNavigationBridge.js";
import { PushOptIn } from "./components/PushOptIn.js";
import { InstallPrompt } from "./components/InstallPrompt.js";
import { UpdateAvailableToast } from "./components/UpdateAvailableToast.js";
import { DrunkFilterDefs } from "./components/DrunkFilterDefs.js";
import { DrunkModeController } from "./components/DrunkModeController.js";
import { LabounModeController } from "./components/LabounModeController.js";
import { DebugOverlay } from "./components/DebugOverlay.js";
import { useAuth } from "./lib/use-auth.js";
import { loadAndApplyTheme } from "./styles/apply-theme.js";
import { captureBaselineVersions } from "./lib/version-tracking.js";

// i18n init — side-effect import (musí být před první renderou)
import "./i18n/index.js";

// ── Public website (lazy) ──────────────────────────────────────────────────────
const PublicLayout = lazy(() =>
  import("./public/PublicLayout.js").then((m) => ({ default: m.PublicLayout })),
);
const LandingPage = lazy(() =>
  import("./public/pages/LandingPage.js").then((m) => ({ default: m.LandingPage })),
);
const AchievementsCatalogPage = lazy(() =>
  import("./public/pages/AchievementsCatalogPage.js").then((m) => ({
    default: m.AchievementsCatalogPage,
  })),
);
const MedalsCatalogPage = lazy(() =>
  import("./public/pages/MedalsCatalogPage.js").then((m) => ({
    default: m.MedalsCatalogPage,
  })),
);
const RanksCatalogPage = lazy(() =>
  import("./public/pages/RanksCatalogPage.js").then((m) => ({
    default: m.RanksCatalogPage,
  })),
);
const BeerRadarPage = lazy(() =>
  import("./beer-radar/pages/BeerRadarPage.js").then((m) => ({ default: m.BeerRadarPage })),
);
const RulesPage = lazy(() =>
  import("./public/pages/RulesPage.js").then((m) => ({ default: m.RulesPage })),
);
const ProgramPage = lazy(() =>
  import("./public/pages/ProgramPage.js").then((m) => ({ default: m.ProgramPage })),
);
const PricingPage = lazy(() =>
  import("./public/pages/PricingPage.js").then((m) => ({ default: m.PricingPage })),
);
const SeasonsOverviewPage = lazy(() =>
  import("./public/pages/SeasonsOverviewPage.js").then((m) => ({ default: m.SeasonsOverviewPage })),
);
const TeamPage = lazy(() =>
  import("./public/pages/TeamPage.js").then((m) => ({ default: m.TeamPage })),
);
const FaqPage = lazy(() =>
  import("./public/pages/FaqPage.js").then((m) => ({ default: m.FaqPage })),
);
const AboutPage = lazy(() =>
  import("./public/pages/AboutPage.js").then((m) => ({ default: m.AboutPage })),
);
const TestimonialsPage = lazy(() =>
  import("./public/pages/TestimonialsPage.js").then((m) => ({ default: m.TestimonialsPage })),
);
const GalleryPage = lazy(() =>
  import("./public/pages/GalleryPage.js").then((m) => ({ default: m.GalleryPage })),
);
const ContestPage = lazy(() =>
  import("./public/pages/ContestPage.js").then((m) => ({ default: m.ContestPage })),
);

// ── Auth + Admin (lazy) ────────────────────────────────────────────────────────
const LiveDashboard = lazy(() =>
  import("./admin/pages/LiveDashboard.js").then((m) => ({ default: m.LiveDashboard })),
);
const DuelNew = lazy(() =>
  import("./admin/pages/DuelNew.js").then((m) => ({ default: m.DuelNew })),
);
const DuelsOverview = lazy(() =>
  import("./admin/pages/DuelsOverview.js").then((m) => ({ default: m.DuelsOverview })),
);
const ScoreboardMonitor = lazy(() =>
  import("./admin/pages/ScoreboardMonitor.js").then((m) => ({ default: m.ScoreboardMonitor })),
);
const SeasonsManagement = lazy(() =>
  import("./admin/pages/SeasonsManagement.js").then((m) => ({ default: m.SeasonsManagement })),
);
const PlayersManagement = lazy(() =>
  import("./admin/pages/PlayersManagement.js").then((m) => ({ default: m.PlayersManagement })),
);
const ContentEditor = lazy(() =>
  import("./admin/pages/ContentEditor.js").then((m) => ({ default: m.ContentEditor })),
);
const ThemeEditor = lazy(() =>
  import("./admin/pages/ThemeEditor.js").then((m) => ({ default: m.ThemeEditor })),
);
const ScoreboardConfigEditor = lazy(() =>
  import("./admin/pages/ScoreboardConfigEditor.js").then((m) => ({ default: m.ScoreboardConfigEditor })),
);
const ChatChannelsManagement = lazy(() =>
  import("./admin/pages/ChatChannelsManagement.js").then((m) => ({ default: m.ChatChannelsManagement })),
);
const UsersManagement = lazy(() =>
  import("./admin/pages/UsersManagement.js").then((m) => ({ default: m.UsersManagement })),
);
const AdminUserDetailPage = lazy(() =>
  import("./admin/pages/AdminUserDetailPage.js").then((m) => ({ default: m.AdminUserDetailPage })),
);
const AdminTeamPage = lazy(() =>
  import("./admin/pages/AdminTeamPage.js").then((m) => ({ default: m.AdminTeamPage })),
);
const RfidCardsManagement = lazy(() =>
  import("./admin/pages/RfidCardsManagement.js").then((m) => ({ default: m.RfidCardsManagement })),
);
const ChangelogHistory = lazy(() =>
  import("./admin/pages/ChangelogHistory.js").then((m) => ({ default: m.ChangelogHistory })),
);
const GalleryManagement = lazy(() =>
  import("./admin/pages/GalleryManagement.js").then((m) => ({ default: m.GalleryManagement })),
);
const StorageManagement = lazy(() =>
  import("./admin/pages/StorageManagement.js").then((m) => ({ default: m.StorageManagement })),
);
const TrainingGalleryManagement = lazy(() =>
  import("./admin/pages/TrainingGalleryManagement.js").then((m) => ({
    default: m.TrainingGalleryManagement,
  })),
);
const ContestManagement = lazy(() =>
  import("./admin/pages/ContestManagement.js").then((m) => ({ default: m.ContestManagement })),
);
const DocumentationPage = lazy(() =>
  import("./admin/pages/DocumentationPage.js").then((m) => ({ default: m.DocumentationPage })),
);
const AdminLanding = lazy(() =>
  import("./admin/pages/AdminLanding.js").then((m) => ({ default: m.AdminLanding })),
);
const AdminLayout = lazy(() =>
  import("./admin/components/AdminLayout.js").then((m) => ({ default: m.AdminLayout })),
);
const CertificatePage = lazy(() =>
  import("./admin/pages/CertificatePage.js").then((m) => ({ default: m.CertificatePage })),
);
const SessionConfigEditor = lazy(() =>
  import("./admin/pages/SessionConfigEditor.js").then((m) => ({ default: m.SessionConfigEditor })),
);
const ProgramEditor = lazy(() =>
  import("./admin/pages/ProgramEditor.js").then((m) => ({ default: m.ProgramEditor })),
);
const FaqEditor = lazy(() =>
  import("./admin/pages/FaqEditor.js").then((m) => ({ default: m.FaqEditor })),
);
const HomepageEditor = lazy(() =>
  import("./admin/pages/HomepageEditor.js").then((m) => ({ default: m.HomepageEditor })),
);
const RankEditor = lazy(() =>
  import("./admin/pages/RankEditor.js").then((m) => ({ default: m.RankEditor })),
);
const MedalEditor = lazy(() =>
  import("./admin/pages/MedalEditor.js").then((m) => ({ default: m.MedalEditor })),
);
const AchievementsManagement = lazy(() =>
  import("./admin/pages/AchievementsManagement.js").then((m) => ({
    default: m.AchievementsManagement,
  })),
);
const ScoreboardPage = lazy(() =>
  import("./scoreboard/ScoreboardPage.js").then((m) => ({ default: m.ScoreboardPage })),
);
const LabounLeaderboardPage = lazy(() =>
  import("./scoreboard/LabounLeaderboardPage.js").then((m) => ({ default: m.LabounLeaderboardPage })),
);
const PlayerCardPage = lazy(() =>
  import("./players/PlayerCardPage.js").then((m) => ({ default: m.PlayerCardPage })),
);
const ChatPage = lazy(() =>
  import("./chat/ChatPage.js").then((m) => ({ default: m.ChatPage })),
);
const LoginPage = lazy(() =>
  import("./auth/LoginPage.js").then((m) => ({ default: m.LoginPage })),
);
const SignupPage = lazy(() =>
  import("./auth/SignupPage.js").then((m) => ({ default: m.SignupPage })),
);

// ── Route loading fallback ─────────────────────────────────────────────────────
function PageLoader() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-muted-foreground text-sm animate-pulse">Načítám…</div>
    </div>
  );
}

export function App() {
  // Načtení admin theme overrides z API (idempotentní; 404 = žádné overrides)
  useEffect(() => {
    void loadAndApplyTheme();
    // Zachyť verze serveru při startu (baseline pro UpdateAvailableToast).
    void captureBaselineVersions();
  }, []);

  return (
    <BrowserRouter>
      <NotificationNavigationBridge />
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public web (čeština defaultně, bez jazykového prefixu) */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/pravidla" element={<RulesPage />} />
              <Route path="/program" element={<ProgramPage />} />
              <Route path="/cenik" element={<PricingPage />} />
              <Route path="/seasons" element={<SeasonsOverviewPage />} />
              <Route path="/team" element={<TeamPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/recenze" element={<TestimonialsPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/soutez" element={<ContestPage />} />
              <Route path="/oceneni" element={<AchievementsCatalogPage />} />
              <Route path="/medaile" element={<MedalsCatalogPage />} />
              <Route path="/hodnosti" element={<RanksCatalogPage />} />
              <Route path="/radar" element={<BeerRadarPage />} />
              {/* Žebříček Labouň módu (nachytané mouchy) — v PublicLayout (má horní lištu). */}
              <Route path="/scoreboard/laboun" element={<LabounLeaderboardPage />} />
            </Route>

            {/* Přejmenováno /tym → /team — redirect kvůli starým odkazům/záložkám. */}
            <Route path="/tym" element={<Navigate to="/team" replace />} />

            {/* Veřejný scoreboard — full-bleed bez headeru */}
            <Route path="/scoreboard/live" element={<ScoreboardPage />} />
            <Route path="/scoreboard/live/:seasonId" element={<ScoreboardPage />} />

            {/* Auth */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />

            {/* Karta hráče */}
            <Route path="/me" element={<PlayerCardPage mode="me" />} />
            <Route path="/players/:id" element={<PlayerCardPage mode="public" />} />

            {/* Chat */}
            <Route path="/chat" element={<ChatPage />} />

            {/* Admin (bartender+) */}
            {/* Live sekce — BEZ horní lišty (fokusované live obrazovky). */}
            <Route path="/admin/live" element={<LiveDashboard />} />
            <Route path="/admin/live/duel/new" element={<DuelNew />} />
            <Route path="/admin/live/duels" element={<DuelsOverview />} />
            <Route path="/admin/live/scoreboard" element={<ScoreboardMonitor />} />
            {/* Zpětná kompatibilita: starý odkaz /admin/session → /admin/security */}
            <Route path="/admin/session" element={<Navigate to="/admin/security" replace />} />
            {/* Landing + ostatní admin stránky — s horní lištou (AdminLayout). */}
            <Route element={<AdminLayout />}>
              <Route path="/admin" element={<AdminLanding />} />
              <Route path="/admin/seasons" element={<SeasonsManagement />} />
              <Route path="/admin/players" element={<PlayersManagement />} />
              <Route path="/admin/content/:key" element={<ContentEditor />} />
              <Route path="/admin/content" element={<ContentEditor />} />
              <Route path="/admin/theme" element={<ThemeEditor />} />
              <Route path="/admin/scoreboard" element={<ScoreboardConfigEditor />} />
              <Route path="/admin/security" element={<SessionConfigEditor />} />
              <Route path="/admin/certificate" element={<CertificatePage />} />
              <Route path="/admin/homepage" element={<HomepageEditor />} />
              <Route path="/admin/program" element={<ProgramEditor />} />
              <Route path="/admin/faq" element={<FaqEditor />} />
              <Route path="/admin/ranks" element={<RankEditor />} />
              <Route path="/admin/medals" element={<MedalEditor />} />
              <Route path="/admin/achievements" element={<AchievementsManagement />} />
              <Route path="/admin/chat" element={<ChatChannelsManagement />} />
              <Route path="/admin/gallery" element={<GalleryManagement />} />
              <Route path="/admin/storage" element={<StorageManagement />} />
              <Route path="/admin/training-gallery" element={<TrainingGalleryManagement />} />
              <Route path="/admin/contests" element={<ContestManagement />} />
              <Route path="/admin/users" element={<UsersManagement />} />
              <Route path="/admin/users/:id" element={<AdminUserDetailPage />} />
              <Route path="/admin/team" element={<AdminTeamPage />} />
              <Route path="/admin/rfid" element={<RfidCardsManagement />} />
              <Route path="/admin/changelog" element={<ChangelogHistory />} />
              <Route path="/admin/documentation" element={<DocumentationPage />} />
            </Route>

            {/* Fallback — vše ostatní jde domů */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        {/* PWA update toast — když nový service worker převezme kontrolu
            (po deploy), nabídne uživateli "Načíst novou verzi" tlačítko.
            Mountujeme nad PushOptIn aby nikoliv lidem zobrazování nepřekáželo. */}
        <UpdateAvailableToast />
        {/* Global PushOptIn banner — zobrazí se pro přihlášené uživatele
            v default permission state. Banner je dismissable a respektuje
            `bw:push:dismissed` flag. */}
        <PushOptInGate />
        {/* PWA install prompt — Chromium `beforeinstallprompt`. Pro všechny
            (i nepřihlášené), dokud appka není nainstalovaná / banner odmítnut. */}
        <InstallPrompt />
        {/* Drunk-mode — SVG filtr (def jednou) + controller (třída na body + scale). */}
        <DrunkFilterDefs />
        <DrunkModeController />
        {/* Labouň mód — overlay s mouchami (for-fun, opt-in). */}
        <LabounModeController />
        {/* Admin debug overlay — chyby z konzole na obrazovce (mobilní PWA bez DevTools).
            Self-gating: nic nerenderuje mimo admina se zapnutým debug módem. */}
        <DebugOverlay />
      </ErrorBoundary>
    </BrowserRouter>
  );
}

/** Wrapper — předá auth token PushOptInu jen pokud uživatel je přihlášený. */
function PushOptInGate() {
  const auth = useAuth();
  if (!auth.isLoggedIn || !auth.token) return null;
  return <PushOptIn token={auth.token} />;
}
